// SellerKYC.jsx — Estate Experts
import React, { useState, useRef, useContext } from 'react';
import {
  FiShield, FiUpload, FiCheck, FiAlertTriangle,
  FiUser, FiMail, FiPhone, FiMapPin, FiFileText,
  FiChevronDown, FiChevronUp, FiX, FiHome,
  FiBriefcase, FiCamera, FiArrowRight
} from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import '../../../assets/css/sellers/dashboard/sellerkyc/sellerkyc.css';
import SellerMasterLayout from 'layouts/SellerMasterLayout';
import AppContext from 'context/AppContext';
import axios from 'axios';
import Swal from 'sweetalert2';

/* ── Status pill ── */
const StatusPill = ({ status }) => {
  const map = {
    Verified: { cls: 'skp-verified', label: '✓ Verified' },
    Pending: { cls: 'skp-pending', label: '⏳ Pending' },
    Incomplete: { cls: 'skp-incomplete', label: '✗ Incomplete' },
    Submitted: { cls: 'skp-submitted', label: '↑ Submitted' },
  };
  const { cls, label } = map[status] || map.Pending;
  return <span className={`skp-pill ${cls}`}>{label}</span>;
};

/* ── Document slot config (accent = brand-kit status colors only) ── */
const DOC_SLOTS = [
  {
    key: 'cnicFront',
    label: 'CNIC Front',
    desc: 'Upload front side of your CNIC',
    icon: <FiFileText size={20} />,
    accent: 'info',
    required: true,
  },
  {
    key: 'cnicBack',
    label: 'CNIC Back',
    desc: 'Upload back side of your CNIC',
    icon: <FiFileText size={20} />,
    accent: 'success',
    required: true,
  },
  {
    key: 'selfie',
    label: 'Selfie with CNIC',
    desc: 'Hold your CNIC next to your face',
    icon: <FiCamera size={20} />,
    accent: 'dark',
    required: true,
  },
];

const ADDRESS_SLOT = {
  key: 'addressProof',
  label: 'Upload Address Proof',
  desc: 'Utility bill, bank statement or residence proof',
  icon: <FiFileText size={22} />,
  accent: 'warning',
  required: false,
};

const SellerKyc = () => {
  const { baseUrl, authToken, loggedInSeller } = useContext(AppContext);

  //───────── PERSONAL / BUSINESS INFO ─────────
  const [fullName, setFullName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [experience, setExperience] = useState('');
  const status = 'pending'; // set internally; not a user-facing field

  //───────── DOCUMENTS (one slot each — matches design) ─────────
  const [documents, setDocuments] = useState({
    cnicFront: null,
    cnicBack: null,
    selfie: null,
    addressProof: null,
  });

  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({ personal: true, documents: true, address: true });
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState({ show: false, msg: '', ok: true });
  const [previewImg, setPreviewImg] = useState(null);

  const fileInputRefs = useRef({});

  /* ── Progress calculation ── */
  const REQUIRED_FIELDS = [businessEmail, businessName, businessPhone, businessAddress];
  const requiredFieldsFilled = REQUIRED_FIELDS.filter((v) => v && v.trim() !== '').length;
  const requiredDocsFilled = DOC_SLOTS.filter((slot) => documents[slot.key]).length;
  const totalRequired = REQUIRED_FIELDS.length + DOC_SLOTS.length; // 5 fields + 3 docs = 8
  const pct = Math.round(((requiredFieldsFilled + requiredDocsFilled) / totalRequired) * 100);

  const docStatus = (slotKey) => (documents[slotKey] ? 'Submitted' : 'Incomplete');

  /* ── Helpers ── */
  const showToast = (msg, ok = true) => {
    setToast({ show: true, msg, ok });
    setTimeout(() => setToast({ show: false, msg: '', ok: true }), 3200);
  };

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ── Upload / remove for a single document slot ── */
  const handleSlotUpload = (slotKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowed.includes(file.type) || file.size > 5 * 1024 * 1024) {
      showToast('Only JPG/PNG/PDF under 5 MB allowed.', false);
      return;
    }
    setDocuments((prev) => ({
      ...prev,
      [slotKey]: { file, url: URL.createObjectURL(file), name: file.name },
    }));
    showToast('File uploaded!');
    e.target.value = '';
  };

  const removeSlot = (slotKey) => {
    setDocuments((prev) => ({ ...prev, [slotKey]: null }));
  };

  /* ── Submit ── */
  const createKyc = () => {
    if (requiredFieldsFilled < REQUIRED_FIELDS.length) {
      showToast('Please complete all required personal information fields.', false);
      return;
    }
    if (requiredDocsFilled < DOC_SLOTS.length) {
      showToast('Please upload CNIC Front, CNIC Back and Selfie with CNIC.', false);
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append('exseller_id', loggedInSeller?.id);
    formData.append('businessEmail', businessEmail);
    formData.append('businessName', businessName);
    formData.append('licenseNumber', licenseNumber);
    formData.append('businessPhone', businessPhone);
    formData.append('businessAddress', businessAddress);
    formData.append('experience', experience);
    if (documents.cnicFront) {
      formData.append('cnic_front', documents.cnicFront.file);
    }
    if (documents.cnicBack) {
      formData.append('cnic_back', documents.cnicBack.file);
    }
    if (documents.selfie) {
      formData.append('selfie_with_cnic', documents.selfie.file);
    }
    if (documents.addressProof) {
      formData.append('address_proof', documents.addressProof.file);
    }
    axios
      .post(`${baseUrl}/seller/sellerkyc/createKyc`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.success) {
          localStorage.setItem('seller_kyc_verified', 'true');
          localStorage.removeItem('seller_kyc_skipped');
          Swal.fire('Success', 'KYC completed successfully', 'success');
        }
        showToast('KYC Uploaded Successfully');
        // RESET FIELDS
        setBusinessEmail('');
        setBusinessName('');
        setLicenseNumber('');
        setBusinessPhone('');
        setBusinessAddress('');
        setExperience('');
        setDocuments({ cnicFront: null, cnicBack: null, selfie: null, addressProof: null });
        setSubmitted(true);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error.response?.data || error.message);
        showToast('Upload Failed', false);
        setLoading(false);
      });
  };

  /* ── Section Head (numbered + color-accented) ── */
  const SectionHead = ({ skey, step, title, subtitle, icon, optional }) => (
    <button className="skc-sec-head" onClick={() => toggle(skey)} type="button">
      <div className="skc-sec-head-left">
        <div className="skc-step-badge">{step}</div>
        <div className="skc-sec-icon">{icon}</div>
        <div className="skc-sec-title-wrap">
          <p className="skc-sec-title">
            {title} {optional && <span className="skc-sec-optional-tag">(Optional)</span>}
          </p>
          <p className="skc-sec-sub">{subtitle}</p>
        </div>
      </div>
      <span className="skc-sec-chevron">
        {expanded[skey] ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
      </span>
    </button>
  );

  /* ── Document upload card ── */
  const DocCard = ({ slot }) => {
    const doc = documents[slot.key];
    return (
      <div className={`skc-doc-card skc-doc-card--${slot.accent} ${doc ? 'skc-doc-card--filled' : ''}`}>
        {doc ? (
          <div className="skc-doc-preview-wrap">
            {doc.file.type.startsWith('image/') ? (
              <img
                src={doc.url}
                alt={slot.label}
                className="skc-doc-preview-img"
                onClick={() => setPreviewImg(doc.url)}
                title="Click to preview"
              />
            ) : (
              <div className="skc-doc-preview-pdf">
                <FiFileText size={22} />
                <span>{doc.name}</span>
              </div>
            )}
            <button
              type="button"
              className="skc-doc-remove"
              onClick={() => removeSlot(slot.key)}
              title="Remove"
            >
              <FiX size={12} />
            </button>
          </div>
        ) : (
          <>
            <div className="skc-doc-icon-circle">{slot.icon}</div>
            <p className="skc-doc-name">{slot.label}</p>
            <p className="skc-doc-desc">{slot.desc}</p>
          </>
        )}

        <label className="skc-doc-upload-btn" htmlFor={`doc-${slot.key}`}>
          <FiUpload size={13} /> Upload File
        </label>
        <input
          id={`doc-${slot.key}`}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={(e) => handleSlotUpload(slot.key, e)}
          style={{ display: 'none' }}
        />

        <p className="skc-doc-hint">JPG, PNG, PDF &nbsp;(Max 5MB)</p>

        {doc && <p className="skc-doc-filename">{doc.name}</p>}
      </div>
    );
  };

  return (
    <SellerMasterLayout pageTitle="Kyc verification">
      {/* Toast */}
      {toast.show && (
        <div className={`sk-toast ${toast.ok ? 'sk-toast-ok' : 'sk-toast-err'}`}>
          {toast.ok ? <FiCheck size={15} /> : <FiAlertTriangle size={15} />}
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Preview modal */}
      {previewImg && (
        <div className="sk-preview-overlay" onClick={() => setPreviewImg(null)}>
          <div className="sk-preview-box" onClick={(e) => e.stopPropagation()}>
            <button className="sk-preview-close" onClick={() => setPreviewImg(null)}>
              <FiX size={20} />
            </button>
            <img src={previewImg} alt="preview" className="sk-preview-img" />
          </div>
        </div>
      )}

      {/* ── KYC Page wrapper ── */}
      <div className="seller-kyc-page">

        {/* Page heading */}
        <div className="sk-page-header">
          <div className="sk-page-header-icon">
            <FiShield size={22} />
          </div>
          <div className="sk-page-header-text">
            <h1 className="sk-page-title">Seller KYC Verification</h1>
            <p className="sk-page-subtitle">
              Complete all steps to get your account verified and start selling on our platform.
            </p>
          </div>
          {submitted && (
            <span className="sk-submitted-badge">
              <MdVerified size={16} /> Submitted for Review
            </span>
          )}
        </div>

        {/* Progress */}
        <div className="sk-progress-label">
          <span>Verification Progress</span>
          <span className="sk-progress-pct">{pct}% Complete</span>
        </div>
        <div className="sk-progress-track">
          <div className="sk-progress-fill" style={{ width: `${pct}%` }} />
        </div>

        {/* Main grid */}
        <div className="sk-main-grid">
          <div className="sk-form-col">

            {/* ══ STEP 1 — Personal Information ══ */}
            <div className="skc-section skc-section--info">
              <SectionHead
                skey="personal"
                step="1"
                title="Personal Information"
                subtitle="Please provide your basic information so we can verify your identity."
                icon={<FiUser size={18} />}
              />
              {expanded.personal && (
                <div className="skc-body">
                  <form className="kyc-form" onSubmit={(e) => e.preventDefault()}>
                    <div className="kyc-form-grid">

                      {/* <div className="skc-field-row">
                        <div className="skc-field-icon"><FiUser size={17} /></div>
                        <div className="skc-field">
                          <label className="skc-label">Full Name <span className="skc-req">*</span></label>
                          <input type="text" value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            placeholder="Enter your full name" />
                        </div>
                      </div> */}

                      <div className="skc-field-row">
                        <div className="skc-field-icon"><FiHome size={17} /></div>
                        <div className="skc-field">
                          <label className="skc-label">Business Name <span className="skc-req">*</span></label>
                          <input type="text" value={businessName}
                            onChange={(e) => setBusinessName(e.target.value)}
                            placeholder="Enter your business name" />
                        </div>
                      </div>

                      <div className="skc-field-row">
                        <div className="skc-field-icon"><FiMail size={17} /></div>
                        <div className="skc-field">
                          <label className="skc-label">Email Address <span className="skc-req">*</span></label>
                          <input type="email" value={businessEmail}
                            onChange={(e) => setBusinessEmail(e.target.value)}
                            placeholder="example@domain.com" />
                        </div>
                      </div>

                      <div className="skc-field-row">
                        <div className="skc-field-icon"><FiPhone size={17} /></div>
                        <div className="skc-field">
                          <label className="skc-label">Business Phone <span className="skc-req">*</span></label>
                          <input type="text" value={businessPhone}
                            onChange={(e) => setBusinessPhone(e.target.value)}
                            placeholder="+92 300 1234567" />
                        </div>
                      </div>

                      <div className="skc-field-row">
                        <div className="skc-field-icon"><FiBriefcase size={17} /></div>
                        <div className="skc-field">
                          <label className="skc-label">Experience</label>
                          <select className="skc-select" value={experience}
                            onChange={(e) => setExperience(e.target.value)}>
                            <option value="">Select your experience</option>
                            <option value="0-1">0–1 years</option>
                            <option value="1-3">1–3 years</option>
                            <option value="3-5">3–5 years</option>
                            <option value="5+">5+ years</option>
                            <option value="10+">10+ years</option>
                          </select>
                        </div>
                      </div>

                      <div className="skc-field-row">
                        <div className="skc-field-icon"><FiMapPin size={17} /></div>
                        <div className="skc-field">
                          <label className="skc-label">Business Address <span className="skc-req">*</span></label>
                          <input type="text" value={businessAddress}
                            onChange={(e) => setBusinessAddress(e.target.value)}
                            placeholder="Enter your business address" />
                        </div>
                      </div>

                      <div className="skc-field-row">
                        <div className="skc-field-icon"><FiFileText size={17} /></div>
                        <div className="skc-field">
                          <label className="skc-label">License Number</label>
                          <input type="text" value={licenseNumber}
                            onChange={(e) => setLicenseNumber(e.target.value)}
                            placeholder="Enter your license number (if any)" />
                        </div>
                      </div>

                    </div>
                  </form>
                </div>
              )}
            </div>

            {/* ══ STEP 2 — Identity Documents ══ */}
            <div className="skc-section skc-section--success">
              <SectionHead
                skey="documents"
                step="2"
                title="Identity Documents"
                subtitle="Upload clear and valid documents for identity verification."
                icon={<FiFileText size={18} />}
              />
              {expanded.documents && (
                <div className="skc-body">
                  <div className="skc-doc-grid">
                    {DOC_SLOTS.map((slot) => (
                      <DocCard key={slot.key} slot={slot} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ══ STEP 3 — Address Proof (Optional) ══ */}
            <div className="skc-section skc-section--warning">
              <SectionHead
                skey="address"
                step="3"
                title="Address Proof"
                subtitle="Upload a document that shows your current address. (Optional)"
                icon={<FiMapPin size={18} />}
                optional
              />
              {expanded.address && (
                <div className="skc-body">
                  <div className="skc-doc-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div style={{ maxWidth: 340, margin: '0 auto', width: '100%' }}>
                      <DocCard slot={ADDRESS_SLOT} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Submit */}
            <button className="sk-submit-btn" onClick={createKyc} disabled={loading}>
              {loading ? (
                'Submitting...'
              ) : (
                <>
                  <FiShield size={16} />
                  <span>Submit Verification</span>
                  <FiArrowRight size={16} />
                </>
              )}
            </button>

          </div>
        </div>
      </div>
    </SellerMasterLayout>
  );
};

export default SellerKyc;